import { useState } from 'react';
import type { Pareja } from '../services/api';
import type { CuadroResuelto } from '../nucleo/cuadro';
import { rangoDeFechas } from '../nucleo/fechas';
import { ORIGEN_PUBLICO } from '../nucleo/sitio';
import { marcadorEnTexto } from '../nucleo/marcador';
import { caminoEnElCuadro, NOMBRE_PUESTO, podioDelCuadro, PUESTOS, type EnPodio, type Puesto } from '../nucleo/podio';
import type { Tema } from '../nucleo/temas';
import { nombrePareja } from './Grupos';
import type { DatosDePlaca } from './placa';
import { EditorPlaca } from './EditorPlaca';
import { Boton, Tarjeta, Titulo } from './ui';

// El podio de una categoría: quién se lleva cada puesto (sale del cuadro) y qué
// se lleva (lo escribió el organizador). Y la imagen para mandar al grupo.

export interface Premio {
  puesto: number;
  descripcion: string;
}

const COLOR_PUESTO: Record<Puesto, string> = { 1: '#e6b84a', 2: '#c9d1d9', 3: '#c7834a' };

export function VistaPremios({
  categoria,
  premios,
  cuadro,
  parejas,
  torneo,
  tema,
  premiosActivos = true,
  linkCompartir,
  recordarPreferencias = true,
}: {
  categoria: string;
  premios: Premio[];
  /** null = el cuadro todavía no se armó: se muestran los premios sin nombres. */
  cuadro: CuadroResuelto | null;
  parejas: Map<number, Pareja>;
  torneo: { nombre: string; sede: string | null; fecha_desde: string; fecha_hasta: string; slug: string };
  tema: Tema;
  premiosActivos?: boolean;
  linkCompartir?: string;
  recordarPreferencias?: boolean;
}) {
  const [placa, setPlaca] = useState(false);
  const podio = cuadro && podioDelCuadro(cuadro);
  const que = (puesto: Puesto) => premios.find((p) => p.puesto === puesto)?.descripcion ?? '';

  const quienes = (puesto: Puesto): EnPodio[] => {
    if (!podio) return [];
    if (puesto === 1) return [podio.campeon];
    if (puesto === 2) return [podio.subcampeon];
    return podio.semifinalistas;
  };
  const nombre = (e: EnPodio) => ('pareja' in e ? nombrePareja(parejas.get(e.pareja)) : e.pendiente);

  // Un puesto se muestra si tiene premio o si tiene a alguien (aunque sea "Ganador de la final").
  const puestos = PUESTOS.filter((p) => que(p) || quienes(p).length > 0);
  const hayCampeona = podio !== null && 'pareja' in podio.campeon;

  const datosDePlaca = (): DatosDePlaca | null => {
    if (!podio || !cuadro || !('pareja' in podio.campeon)) return null;
    const campeona = podio.campeon.pareja;
    return {
      torneo: torneo.nombre,
      cuando: [rangoDeFechas(torneo.fecha_desde, torneo.fecha_hasta), torneo.sede].filter(Boolean).join(' · '),
      categoria,
      tema,
      campeona: nombre(podio.campeon),
      subcampeona: 'pareja' in podio.subcampeon ? nombre(podio.subcampeon) : null,
      semifinalistas: podio.semifinalistas.filter((e) => 'pareja' in e).map(nombre),
      premios: { 1: que(1), 2: que(2), 3: que(3) },
      camino: caminoEnElCuadro(cuadro, campeona).map((p) => ({
        ronda: p.ronda,
        rival: nombrePareja(parejas.get(p.rival)),
        marcador: marcadorEnTexto(p.marcador, p.desde),
      })),
      link: linkCompartir ?? `${ORIGEN_PUBLICO}/t/${torneo.slug}`,
    };
  };

  return (
    <>
      <Tarjeta>
        <Titulo extra={categoria}>{premiosActivos ? 'Premios' : 'Podio'}</Titulo>
        {puestos.length === 0 ? (
          <p className="py-4 text-center text-sm text-(--color-apagado)">Los premios aparecen acá cuando los carguen los organizadores.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {puestos.map((p) => (
              <li
                key={p}
                className="grid grid-cols-[2.25rem_minmax(0,1fr)] items-center gap-3 rounded-xl border-2 px-3 py-2.5"
                style={{ borderColor: COLOR_PUESTO[p] }}
              >
                <span className="text-center font-titulo text-3xl" style={{ color: COLOR_PUESTO[p] }}>
                  {p}º
                </span>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: COLOR_PUESTO[p] }}>
                    {NOMBRE_PUESTO[p]}
                  </p>
                  {quienes(p).map((e, i) => (
                    <p key={i} className={'pareja' in e ? 'font-bold' : 'italic text-(--color-apagado)'}>
                      {nombre(e)}
                    </p>
                  ))}
                  {que(p) && <p className="text-sm text-(--color-apagado)">{que(p)}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}
        {podio && !hayCampeona && puestos.length > 0 && (
          <p className="mt-2 text-xs text-(--color-apagado)">Cada nombre aparece cuando se juega el partido que lo define.</p>
        )}
        {hayCampeona && !placa && (
          <div className="mt-3">
            <Boton chico onClick={() => setPlaca(true)}>Armar imagen para compartir</Boton>
          </div>
        )}
      </Tarjeta>

      {placa && <EditorPlaca datos={datosDePlaca()} archivo={`${torneo.slug}-${categoria}`} alCerrar={() => setPlaca(false)} recordarPreferencias={recordarPreferencias} />}
    </>
  );
}

